(() => {
  const selector = '.card-gallery slideshow-component[infinite]';
  const gestures = new Map();
  const touchGestures = new Map();
  const minimumSwipeDistance = 40;
  let normalizationFrame = 0;

  const normalizeSlides = () => {
    for (const slideshow of document.querySelectorAll(selector)) {
      if (slideshow.getAttribute('disabled') === 'true') continue;

      const variantSlides = new Set(
        slideshow.querySelectorAll('slideshow-slide[variant-image]')
      );
      if (variantSlides.size === 0) continue;

      for (const slide of slideshow.querySelectorAll('slideshow-slide')) {
        if (variantSlides.has(slide)) {
          slide.removeAttribute('hidden');
          slide.removeAttribute('variant-image');
        } else {
          slide.setAttribute('hidden', '');
          slide.setAttribute('aria-hidden', 'true');
        }
      }
    }
  };

  const scheduleSlideNormalization = () => {
    if (normalizationFrame) return;

    normalizationFrame = requestAnimationFrame(() => {
      normalizationFrame = 0;
      normalizeSlides();
    });
  };

  /* Horizon's Slideshow never finishes connecting for a card gallery that is
     present when the page first loads, so `#setupSlideshow` never runs, there
     is no Scroller, and `select()` returns at its own `if (!this.#scroll)`
     guard. That is what killed the second-image-on-hover: the setting is on,
     card-gallery.liquid does emit on:pointerenter="/previewImage", and
     previewImage does call select, which then silently does nothing. Mouse drag
     between mockups is gone for the same reason, since its mousedown listener
     is added in that same setup.

     Measured on the live store 18.09.2026: no `.card-gallery
     slideshow-component` anywhere carries the `disabled` attribute that
     `#setupSlideshow` always writes, on the collection grid or inside the
     recommendations row. The one slideshow on a product page that DOES carry it
     is the recommendations carousel itself, which arrives later through the
     Section Rendering API. So everything present at first paint fails and
     everything injected afterwards succeeds. Same on the 31.08 QA theme, so
     this is not something we shipped in September; it came in with a Horizon
     version, like the inert regression hush-slide-inert.liquid restores.

     Taking the element out of the DOM and putting it straight back runs
     connectedCallback again, this time against a finished subtree, and Horizon
     sets itself up properly. Nothing here reimplements hover or adds controls:
     it only lets Horizon's own code run. */
  const revived = new WeakSet();

  const reviveSlideshows = () => {
    for (const slideshow of document.querySelectorAll(selector)) {
      if (revived.has(slideshow)) continue;
      revived.add(slideshow);

      const parent = slideshow.parentElement;
      if (!parent) continue;

      const next = slideshow.nextSibling;
      slideshow.remove();
      parent.insertBefore(slideshow, next);

      /* Horizon disables a nested gallery, and slideshow-styles.liquid turns
         that into overflow: hidden on its scroller, which would take the finger
         swipe away from the cards in the recommendations row. The Scroller it
         just built is what we came for; the lock is not. */
      if (slideshow.getAttribute('disabled') === 'true') {
        slideshow.removeAttribute('disabled');
      }
    }
  };

  normalizeSlides();
  reviveSlideshows();

  new MutationObserver(() => {
    scheduleSlideNormalization();
    reviveSlideshows();
  }).observe(document.body, {
    childList: true,
    subtree: true,
  });

  const getState = (slideshow) => {
    const slides = slideshow.slides || [];
    const scroller = slideshow.refs?.scroller;
    let currentIndex = -1;
    let nearestDistance = Infinity;

    if (scroller) {
      const viewport = scroller.getBoundingClientRect();
      const center = viewport.left + viewport.width / 2;
      if (viewport.width > 0) {
        slides.forEach((slide, index) => {
          const bounds = slide.getBoundingClientRect();
          const distance = Math.abs(bounds.left + bounds.width / 2 - center);
          if (bounds.width > 0 && distance < nearestDistance) {
            currentIndex = index;
            nearestDistance = distance;
          }
        });
      }
    }

    return {
      currentIndex,
      lastIndex: slides.length - 1,
    };
  };

  const clickArrow = (slideshow, direction) => {
    const ref = direction === 'forward' ? 'next' : 'previous';
    const arrow = slideshow.querySelector(`[ref="${ref}"]`);

    restoreSnapPoints(slideshow);

    if (arrow instanceof HTMLButtonElement) {
      arrow.click();
      return;
    }

    /* A card gallery renders no arrows. Horizon only asks for them when a
       product has media that is NOT attached to a variant, and every Hush
       Hollow mockup is bound to a color variant, so the count is 0 across the
       catalog and this whole file's wheel gesture had nothing to click. Call
       Horizon's own select instead, which is what the arrow would have done. */
    slideshow.select?.(
      direction === 'forward' ? slideshow.nextIndex : slideshow.previousIndex
    );
  };

  const completeSwipe = (
    slideshow,
    startX,
    endX,
    startedAtStart,
    startedAtEnd
  ) => {
    if (!slideshow || !slideshow.isConnected) return;

    const distance = startX - endX;

    if (Math.abs(distance) < minimumSwipeDistance) return;

    if (startedAtEnd && distance > 0) {
      requestAnimationFrame(() => {
        clickArrow(slideshow, 'forward');
      });
    } else if (startedAtStart && distance < 0) {
      requestAnimationFrame(() => {
        clickArrow(slideshow, 'backward');
      });
    }
  };

  /* Keep touch scrolling native and restrict its snap destinations to the
     nearest image and its neighbours. Geometry stays current between images;
     aria-hidden does not. Restore before arrow navigation or after settling.
     Snap destinations do not guarantee the motion feels smooth on a phone. */
  // ponytail: 200ms without scroll events estimates settling when scrollend is unavailable.
  const snapRestoreDelay = 200;
  const snapRestores = new WeakMap();
  const hasActiveTouch = (slideshow) =>
    Array.from(touchGestures.values()).some((gesture) => gesture.slideshow === slideshow);

  const restoreSnapPoints = (slideshow) => {
    const timer = snapRestores.get(slideshow);
    if (timer) clearTimeout(timer);
    snapRestores.delete(slideshow);
    for (const slide of slideshow.querySelectorAll('slideshow-slide')) {
      slide.style.scrollSnapAlign = '';
    }
  };

  const restrictSnapPoints = (slideshow, index) => {
    const slides = slideshow.slides;
    if (!slides || slides.length < 3 || index < 0 || index >= slides.length) return;
    snapRestores.set(slideshow, 0);
    slides.forEach((slide, i) => {
      slide.style.scrollSnapAlign = Math.abs(i - index) <= 1 ? '' : 'none';
    });
  };

  const scheduleSnapRestore = (slideshow) => {
    if (!snapRestores.has(slideshow) || hasActiveTouch(slideshow)) return;
    const timer = snapRestores.get(slideshow);
    if (timer) clearTimeout(timer);
    snapRestores.set(
      slideshow,
      setTimeout(() => restoreSnapPoints(slideshow), snapRestoreDelay)
    );
  };

  document.addEventListener(
    'scroll',
    (event) => {
      if (!(event.target instanceof Element)) return;
      const slideshow = event.target.closest(selector);
      if (slideshow?.refs?.scroller === event.target) scheduleSnapRestore(slideshow);
    },
    { capture: true, passive: true }
  );

  document.addEventListener(
    'scrollend',
    (event) => {
      if (!(event.target instanceof Element)) return;
      const slideshow = event.target.closest(selector);
      if (
        slideshow?.refs?.scroller === event.target &&
        snapRestores.has(slideshow) && !hasActiveTouch(slideshow)
      ) {
        restoreSnapPoints(slideshow);
      }
    },
    true
  );

  document.addEventListener(
    'pointerdown',
    (event) => {
      if (event.pointerType === 'touch') return;
      if (!(event.target instanceof Element)) return;

      const slideshow = event.target.closest(selector);
      if (!slideshow) return;

      const { currentIndex, lastIndex } = getState(slideshow);

      gestures.set(event.pointerId, {
        slideshow,
        startX: event.clientX,
        startedAtStart: currentIndex === 0,
        startedAtEnd: currentIndex === lastIndex,
      });
    },
    true
  );

  document.addEventListener(
    'pointerup',
    (event) => {
      if (event.pointerType === 'touch') return;

      const gesture = gestures.get(event.pointerId);
      gestures.delete(event.pointerId);

      if (!gesture) return;

      completeSwipe(
        gesture.slideshow,
        gesture.startX,
        event.clientX,
        gesture.startedAtStart,
        gesture.startedAtEnd
      );
    },
    true
  );

  document.addEventListener(
    'pointercancel',
    (event) => {
      gestures.delete(event.pointerId);
    },
    true
  );

  document.addEventListener(
    'touchstart',
    (event) => {
      if (!(event.target instanceof Element)) return;

      const slideshow = event.target.closest(selector);
      if (!slideshow || slideshow.getAttribute('disabled') === 'true') return;

      const touch = event.changedTouches[0];
      if (!touch) return;

      const { currentIndex, lastIndex } = getState(slideshow);
      restoreSnapPoints(slideshow);
      if (currentIndex < 0) return;
      restrictSnapPoints(slideshow, currentIndex);

      touchGestures.set(touch.identifier, {
        slideshow,
        startX: touch.clientX,
        startedAtStart: currentIndex === 0,
        startedAtEnd: currentIndex === lastIndex,
      });
    },
    {
      capture: true,
      passive: true,
    }
  );

  document.addEventListener(
    'touchend',
    (event) => {
      for (const touch of event.changedTouches) {
        const gesture = touchGestures.get(touch.identifier);
        touchGestures.delete(touch.identifier);

        if (!gesture) continue;

        scheduleSnapRestore(gesture.slideshow);
        completeSwipe(
          gesture.slideshow,
          gesture.startX,
          touch.clientX,
          gesture.startedAtStart,
          gesture.startedAtEnd
        );
      }
    },
    {
      capture: true,
      passive: true,
    }
  );

  document.addEventListener(
    'touchcancel',
    (event) => {
      for (const touch of event.changedTouches) {
        const gesture = touchGestures.get(touch.identifier);
        touchGestures.delete(touch.identifier);
        if (gesture) scheduleSnapRestore(gesture.slideshow);
      }
    },
    {
      capture: true,
      passive: true,
    }
  );

  /* One slide per wheel gesture, 2026-09-05. The card gallery is a native
     scroll-snap container, and a horizontal wheel or trackpad flick arrives as
     a burst of many small deltaX events. Left to the browser, that burst is a
     fling that carries the card past two or three mockups before it snaps.
     scroll-snap-stop: always did not stop it. So the gesture is owned here:
     the native scroll is cancelled, the deltas are summed, and the arrow is
     clicked once per gesture.

     Three versions of "when does the next gesture start" failed on the
     owner's machine on 05.09, all traced on the preview afterwards:
     1. end on 250ms silence: trackpad inertia keeps sending events for up to
        a second, so the second flick was swallowed ("huge delay");
     2. new gesture when an event is 1.5x the last: tail jitter (4, 9, 3, 12)
        tripped it;
     3. reset on a 100ms gap: the end of the tail is SPARSE, gaps over 100ms
        between 1px to 3px events, so every gap opened a fresh gesture and the
        tiny events summed to the threshold again and again ("loops through
        all the mockups").
     The rule now: the gesture state lives until 500ms of silence. While a
     gesture has acted, a new one starts only if at least 150ms have passed
     since the click AND the event is at least half as strong as this
     gesture's peak AND (the tail had decayed below half the peak, or the
     event is at least as strong as the last one, which is what equal mouse
     notches look like). A direction flip of a real size also starts one. A
     fresh gesture acts once its sum reaches 10px and it has seen one event
     of at least 6px, so a sparse late tail can never add up to a move.
     Known trade-off: a second flick weaker than half the first, inside the
     first one's inertia tail, waits for the 500ms silence. */
  const wheelGestures = new WeakMap();
  const wheelGestureGap = 500;
  const wheelThreshold = 10;
  const wheelMinEvent = 6;
  const wheelMinInterval = 150;

  const normalizeDelta = (event) => {
    if (event.deltaMode === 1) return event.deltaX * 40;
    if (event.deltaMode === 2) return event.deltaX * 800;
    return event.deltaX;
  };

  const freshWheelState = () => ({
    sum: 0,
    acted: false,
    dir: 0,
    peak: 0,
    lastMag: 0,
    actedAt: 0,
    timer: 0,
  });

  document.addEventListener(
    'wheel',
    (event) => {
      if (!(event.target instanceof Element)) return;
      if (Math.abs(event.deltaX) <= Math.abs(event.deltaY)) return;

      const slideshow = event.target.closest(selector);
      if (!slideshow || slideshow.getAttribute('disabled') === 'true') return;

      event.preventDefault();

      const delta = normalizeDelta(event);
      const mag = Math.abs(delta);
      const dir = Math.sign(delta);

      let state = wheelGestures.get(slideshow) || freshWheelState();
      clearTimeout(state.timer);

      if (state.acted) {
        const settled = performance.now() - state.actedAt >= wheelMinInterval;
        const strong = mag >= Math.max(wheelMinEvent, state.peak * 0.5);
        const decayed = state.lastMag < state.peak * 0.5;
        const restarted =
          settled && strong && (decayed || mag >= state.lastMag);
        const flipped = settled && dir !== state.dir && mag >= wheelMinEvent;

        if (!restarted && !flipped) {
          state.lastMag = mag;
          state.peak = Math.max(state.peak, mag);
          state.timer = setTimeout(() => {
            wheelGestures.delete(slideshow);
          }, wheelGestureGap);
          wheelGestures.set(slideshow, state);
          return;
        }

        state = freshWheelState();
      }

      state.sum += delta;
      state.dir = dir;
      state.lastMag = mag;
      state.peak = Math.max(state.peak, mag);
      state.timer = setTimeout(() => {
        wheelGestures.delete(slideshow);
      }, wheelGestureGap);
      wheelGestures.set(slideshow, state);

      if (Math.abs(state.sum) < wheelThreshold) return;
      if (state.peak < wheelMinEvent) return;

      state.acted = true;
      state.actedAt = performance.now();
      clickArrow(slideshow, state.sum > 0 ? 'forward' : 'backward');
    },
    {
      capture: true,
      passive: false,
    }
  );
})();
